import { useGetPlacesByPlaceId, useGetPlacesList } from "@birdwatch/client";
import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo, useState } from "react";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { PlaceTypeIcon } from "@/components/ui/status/place-type-icon";
import { Typography } from "@/components/ui/typography";
import { renderPlaceAddress } from "@/features/office/places/_libs/utils";
import type { PlaceType } from "@/types/openapi-enum";

type PlaceSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
  "data-test"?: string;
};

// Invariant: platform place DTOs constrain `type` to PLACE_TYPE_VALUES.
function placeOption(place: any): ComboboxOption {
  const address = renderPlaceAddress(place);
  return {
    value: place.id,
    label: address,
    keywords: [address],
    data: place,
    detail: (
      <div className="flex grow items-center gap-2">
        <PlaceTypeIcon
          className="size-5 text-text-dark-high"
          type={place.type as PlaceType}
        />
        <Typography size="sm">{address}</Typography>
      </div>
    ),
  };
}

export default function PlaceSelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: PlaceSelectProps) {
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 300);

  const result = useGetPlacesList()
    .search(debouncedKeyword ? debouncedKeyword : undefined)
    .sort("address1")
    .take(10)
    .getInfinite();

  const places = result?.data?.pages.flatMap((page) => page.data) ?? [];
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = result;

  // Off-page resolution (Trap T2): the selected id may not be on the loaded
  // infinite page, so `.find` over `options` can miss it — resolve the trigger
  // label/icon from a single-place fetch independent of the paged list.
  const { data: selectedPlace } = useGetPlacesByPlaceId({
    placeId: value || "",
  }).get({
    enabled: !!value,
  });

  const options = useMemo<ComboboxOption[]>(
    () => places.map(placeOption),
    [places],
  );

  return (
    <Combobox
      data-test="place-select-trigger"
      {...props}
      value={value}
      onChange={onChange}
      options={options}
      search={keyword}
      onSearchChange={setKeyword}
      shouldFilter={false}
      hasMore={hasNextPage}
      isLoadingMore={isFetchingNextPage}
      onLoadMore={fetchNextPage}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
      disabled={disabled}
      className={className}
      placeholder="Search for a place by address"
      searchPlaceholder="Search..."
      emptyText="No results found."
      renderTrigger={(selected) => {
        // list place (option.data) or the off-page single-fetch detail
        const place: any = selected?.data ?? selectedPlace;
        // Invariant: both place projections constrain `type` to PLACE_TYPE_VALUES.
        return place ? (
          <div className="flex grow items-center gap-2">
            <PlaceTypeIcon
              className="size-5 text-text-dark-high"
              type={place.type as PlaceType}
            />
            <Typography size="sm">{renderPlaceAddress(place)}</Typography>
          </div>
        ) : (
          <Typography size="sm" className="text-text-dark-min">
            Search for a place by address
          </Typography>
        );
      }}
    />
  );
}
